import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { organizationService } from '../services/organizations';
import { useOrganization } from '../contexts/OrganizationContext';
import { useToast } from '../hooks/use-toast';
import {
  Loader2,
  Check,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Link2,
  ShoppingCart,
} from 'lucide-react';

export const SettingsPage: React.FC = () => {
  const { toast } = useToast();
  const { refreshOrganizations } = useOrganization();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [retailosApiKey, setRetailosApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [amazonEnabled, setAmazonEnabled] = useState(false);
  const [isTogglingAmazon, setIsTogglingAmazon] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [retailos, amazon] = await Promise.all([
          organizationService.getRetailOSSettings(),
          organizationService.getAmazonSettings(),
        ]);
        setRetailosApiKey(retailos.retailos_api_key || '');
        setIsConfigured(retailos.retailos_configured);
        setAmazonEnabled(amazon.amazon_enabled);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await organizationService.updateRetailOSSettings(retailosApiKey || null);
      setIsConfigured(result.retailos_configured);
      toast({
        title: 'Settings Saved',
        description: 'RetailOS integration settings have been updated',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleAmazon = async (enabled: boolean) => {
    setIsTogglingAmazon(true);
    setAmazonEnabled(enabled);
    try {
      await organizationService.updateAmazonSettings(enabled);
      await refreshOrganizations();
      toast({
        title: enabled ? 'Amazon Export Enabled' : 'Amazon Export Disabled',
        description: enabled
          ? 'Amazon enrichment and export features are now visible'
          : 'Amazon-specific fields and buttons are now hidden',
      });
    } catch (err: any) {
      setAmazonEnabled(!enabled);
      toast({
        title: 'Error',
        description: err.message || 'Failed to update setting',
        variant: 'destructive',
      });
    } finally {
      setIsTogglingAmazon(false);
    }
  };

  const handleClear = async () => {
    setIsSaving(true);
    try {
      await organizationService.updateRetailOSSettings(null);
      setRetailosApiKey('');
      setIsConfigured(false);
      toast({
        title: 'Settings Cleared',
        description: 'RetailOS API key has been removed',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to clear settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Integrations</h1>
          <p className="text-muted-foreground mt-2">
            Connect external services to enhance your workflow
          </p>
        </div>
      </div>

      {/* Amazon Export Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 bg-opacity-10 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <CardTitle>Amazon Export</CardTitle>
                <CardDescription>
                  Enable AI-powered product enrichment and export for Amazon listings
                </CardDescription>
              </div>
            </div>
            <Switch
              id="amazon-toggle"
              checked={amazonEnabled}
              onCheckedChange={handleToggleAmazon}
              disabled={isTogglingAmazon}
            />
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            When enabled, products will show Amazon-specific fields (material, size hints)
            and export buttons for generating Amazon-ready listings with AI enrichment.
          </p>
        </CardContent>
      </Card>

      {/* RetailOS Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500 bg-opacity-10 rounded-lg">
              <Link2 className="h-5 w-5 text-indigo-500" />
            </div>
            <div>
              <CardTitle>RetailOS Integration</CardTitle>
              <CardDescription>
                Connect to RetailOS to push approved products automatically
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {isConfigured && (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                RetailOS integration is configured and active
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="retailos_api_key">API Key</Label>
            <div className="relative">
              <Input
                id="retailos_api_key"
                type={showApiKey ? 'text' : 'password'}
                value={retailosApiKey}
                onChange={(e) => setRetailosApiKey(e.target.value)}
                placeholder="stk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showApiKey ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter your RetailOS Studio API key. This key starts with "stk_".
            </p>
          </div>

          <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>How it works:</strong> Once configured, products with both Media and Content approval
              will show a "Push to RetailOS" button. Clicking it sends the product data including
              images, barcode, and descriptions to your RetailOS store.
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={isSaving || !retailosApiKey}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
            {isConfigured && (
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={isSaving}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                Clear API Key
              </Button>
            )}
          </div>

          <div className="pt-4 border-t">
            <a
              href="https://retailos.tinyscale.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              Go to RetailOS Dashboard
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
